import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useSelector } from "react-redux";

import { AppState } from "shared/data/reducers";
import { dayMonthFormat } from "shared/lib/datetime";
import { formattedMessageParams } from "shared/messages";
import FlightOffer from "shared/types/flightOffer";

const { priceLabel } = defineMessages({
  priceLabel: formattedMessageParams("ShareModal.price"),
});

type Props = {
  flightUrl: string;
  flightPricing: FlightOffer["FlightCombinations"][number]["PricingDetail"]["FlightPricing"];
  flightOriginAirport: string;
  flightDestinationAirport: string;
  departureDate: string;
  returnDate: string;
  searchQuery: AppState["storage"]["searchQuery"];
};

export type ShareInfo = {
  url: string;
  title: string;
  aditionalInfo: string;
};

const useNthFlightShareInfo = ({
  flightUrl,
  flightPricing,
  flightOriginAirport,
  flightDestinationAirport,
  departureDate,
  returnDate,
  searchQuery,
}: Props): ShareInfo => {
  const intl = useIntl();
  const [windowOrigin, setWindowOrigin] = useState("");

  useEffect(() => {
    setWindowOrigin(window.location.origin);
  }, []);

  const { currency, defaultCountry, airportDestinations } = useSelector(
    (appState: AppState) => ({
      currency: appState.requestorConfig.currency,
      defaultCountry:
        appState.storage.frontendSettings?.dealerFrontendSettings
          ?.defaultCountry ?? "",
      airportDestinations: appState.storage.airportDestinations,
    })
  );

  const cachedOriginAirport = useMemo(
    () => airportDestinations.find(({ Code }) => Code === flightOriginAirport),
    [airportDestinations, flightOriginAirport]
  );

  const cachedDestinationAirport = useMemo(
    () =>
      airportDestinations.find(({ Code }) => Code === flightDestinationAirport),
    [airportDestinations, flightDestinationAirport]
  );

  const originAirport = cachedOriginAirport;
  const destinationAirport = cachedDestinationAirport;

  const isMultiCity =
    Object.keys(searchQuery).includes("flight_1_origin") &&
    Object.keys(searchQuery).includes("flight_1_destination");

  const currencySymbol = currency === "CZK" ? "Kč" : currency;

  const flightPlacement = `${originAirport?.$t} - ${destinationAirport?.$t}`;

  const flightPrice = `${intl.formatMessage(priceLabel)} ${Number(
    flightPricing.FlightPrice.FullPrice
  ).toFixed(0)} ${currencySymbol}`;

  const showReturnDate =
    isMultiCity || !returnDate
      ? ""
      : ` - ${moment(returnDate).format(dayMonthFormat(defaultCountry))}`;

  const flightDates = `${moment(departureDate).format(
    dayMonthFormat(defaultCountry)
  )}${showReturnDate}`;

  const aditionalInfo = `${flightPlacement}, ${flightDates}, ${flightPrice}`;

  return {
    url: `${windowOrigin}${flightUrl}`,
    title: aditionalInfo,
    aditionalInfo,
  };
};

export default useNthFlightShareInfo;
